import React, { useState, useEffect, useCallback, useRef } from 'react'

import ProfilePicture from 'components/profile/profile-picture'
import SmoothCollapse from 'react-smooth-collapse'

import { connect } from 'react-redux'

import Icon from 'components/icon/icon'

import {
    setStatus
} from 'state/actions'

import {
    doClickEventListeners,
} from 'utils'

const statuses = [
    {
        key: null,
        label: 'Online',
        description: 'Ready to chat'
    }, {
        key: 'AWAY',
        label: 'Away',
        description: 'Away from keyboard'
    }, {
        key: 'BUSY',
        label: 'Busy',
        description: 'Busy with something'
    }, {
        key: 'OFFLINE',
        label: 'Offline',
        description: 'Offline'
    }
]

const customStatusColors = [
    '#22C55E', // online / active - green
    '#EF4444', // error / offline - red
    '#F59E0B', // away / idle - amber
    '#3B82F6', // busy / info - blue
    '#8B5CF6', // custom / special - purple
    '#EC4899', // notification - pink
    '#14B8A6', // typing / connecting - teal
    '#F97316', // warning - orange
    '#6B7280', // inactive / muted - gray
    '#06B6D4', // in-progress - cyan
]

const BigChatStatus = ({
    userData,
    setStatus
}) => {
    const [expanded, setExpanded] = useState(false)
    const [customEditing, setCustomEditing] = useState(false)
    const [customLabel, setCustomLabel] = useState(userData.status_custom_label || '')
    const [customDescription, setCustomDescription] = useState(userData.status_custom_description || '')
    const [customColor, setCustomColor] = useState(userData.status_custom_color || customStatusColors[0])
    const outerRef = useRef()

    const isCustomActive = userData.status_if_online === 'CUSTOM'
    const currentStatus = statuses.find(status => status.key === userData.status_if_online)

    const updateStatus = (key, custom) => {
        setStatus(userData, key, custom)

        setTimeout(() => {
            setExpanded(false)
        }, 200)
    }

    const openCustomEditor = () => {
        setCustomLabel(userData.status_custom_label || '')
        setCustomDescription(userData.status_custom_description || '')
        setCustomColor(userData.status_custom_color || customStatusColors[0])
        setCustomEditing(true)
    }

    const saveCustomStatus = () => {
        if (!customLabel.trim()) {
            return
        }

        updateStatus('CUSTOM', {
            label: customLabel.trim(),
            description: customDescription.trim(),
            color: customColor
        })

        setCustomEditing(false)
    }

    const handleClickOutside = useCallback(e => {
        if (!outerRef.current) {
            return
        }

        if (!e || !outerRef.current.contains(e.target)) {
            setExpanded(false)
        }
    }, [])

    useEffect(() => {
        doClickEventListeners(handleClickOutside)

        return () => {
            doClickEventListeners(handleClickOutside, true)
        }
    }, [])

    useEffect(() => {
        if (!expanded) {
            setCustomEditing(false)
        }
    }, [expanded])

    return (
        <div
            className='big-chat-status'
            ref={outerRef}
        >
            <button
                onClick={() => setExpanded(!expanded)}
                className={expanded ? 'open' : ''}
            >
                <div>
                    <em
                        className={`online-dot ${isCustomActive ? '' : userData.status_if_online} visible`}
                        style={isCustomActive ? {
                            background: userData.status_custom_color,
                            boxShadow: `0px 0px 10px ${userData.status_custom_color}`
                        } : undefined}
                    />
                    <ProfilePicture user={userData} isTiny={true} />
                </div>
                <div>
                    <h4 dangerouslySetInnerHTML={{ __html: `${userData.first_name} ${userData.last_name}` }} />
                    {isCustomActive &&
                        <span>
                            {userData.status_custom_label}
                        </span>
                    }
                    {!isCustomActive && currentStatus &&
                        <span>
                            {currentStatus.label}
                        </span>
                    }
                </div>
                <Icon name={`fe-chevron-down`} />
            </button>
            <SmoothCollapse expanded={expanded}>
                <ul className='status-dropdown'>
                    {statuses.map(({
                        key,
                        label,
                        description
                    }) => {
                        const isActive = !isCustomActive && currentStatus ? currentStatus.key === key : false

                        return (
                            <li key={key}>
                                <button
                                    onClick={() => updateStatus(key)}
                                    className={isActive ? 'active' : ''}
                                >
                                    <h4>
                                        {label}
                                    </h4>
                                    <span>
                                        {description}
                                    </span>
                                </button>
                            </li>
                        )
                    })}
                    <li className='custom-status'>
                        {customEditing ? (
                            <div className='custom-status-editor'>
                                <input
                                    type='text'
                                    placeholder='Status label'
                                    value={customLabel}
                                    onChange={e => setCustomLabel(e.target.value)}
                                    maxLength={30}
                                    autoFocus
                                />
                                <input
                                    type='text'
                                    placeholder='Description (optional)'
                                    value={customDescription}
                                    onChange={e => setCustomDescription(e.target.value)}
                                    maxLength={60}
                                />
                                <div className='custom-status-colors'>
                                    {customStatusColors.map(color => (
                                        <button
                                            key={color}
                                            type='button'
                                            className={`custom-status-color ${customColor === color ? 'active' : ''}`}
                                            style={{ background: color }}
                                            onClick={() => setCustomColor(color)}
                                        />
                                    ))}
                                </div>
                                <button
                                    type='button'
                                    className='custom-status-save'
                                    disabled={!customLabel.trim()}
                                    onClick={saveCustomStatus}
                                >
                                    Set status
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={openCustomEditor}
                                className={isCustomActive ? 'active' : ''}
                            >
                                <h4>
                                    {isCustomActive ? userData.status_custom_label : 'Custom status'}
                                </h4>
                                <span>
                                    {isCustomActive ? (userData.status_custom_description || 'Custom status') : 'Set your own status'}
                                </span>
                            </button>
                        )}
                    </li>
                </ul>
            </SmoothCollapse>
        </div>
    )
}


const mapStateToProps = state => {
    return {

    }
}

export default connect(mapStateToProps, {
    setStatus
})(BigChatStatus)